/**
 * Hook unico de carga de datos.
 *
 * Una sola forma de pedir a la API en todo el sitio: cargando, listo o error,
 * con la peticion abortada si la pantalla se desmonta o si cambian los datos que
 * la identifican. Asi ninguna vista inventa sus propias banderas ni deja una
 * respuesta vieja pisando a la nueva.
 *
 * Los hooks llevan el prefijo `use` (y no `usar`) porque es lo que exigen las
 * reglas de React; el resto del codigo se mantiene en espanol.
 */
import { useEffect, useState } from 'react';

import type { ResultadoPeticion } from '../tipos/api';
import { aErrorApi } from '../utilidades/error-api';

const INICIAL = { estado: 'cargando', datos: null, error: null } as const;

/**
 * @param pedir  funcion que hace la llamada; recibe la senal de aborto.
 * @param claves valores que, al cambiar, obligan a pedir de nuevo.
 */
export function usePeticion<T>(
  pedir: (senal: AbortSignal) => Promise<T>,
  claves: ReadonlyArray<string | number | boolean | undefined>,
): ResultadoPeticion<T> {
  const [resultado, setResultado] = useState<ResultadoPeticion<T>>(INICIAL);

  useEffect(() => {
    const control = new AbortController();
    setResultado(INICIAL);

    pedir(control.signal)
      .then((datos) => {
        if (control.signal.aborted) return;
        setResultado({ estado: 'listo', datos, error: null });
      })
      .catch((error: unknown) => {
        if (control.signal.aborted) return;
        setResultado({ estado: 'error', datos: null, error: aErrorApi(error) });
      });

    return () => {
      control.abort();
    };
    // `pedir` se redefine en cada render; las claves son la identidad real de la peticion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, claves);

  return resultado;
}
